import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Mail, FileText, Clock, CheckCircle, XCircle, 
  RefreshCw, ExternalLink, Lock, LogOut, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const applicationTypes = [
  { value: 'musicians', label: 'Musicians & DJs' },
  { value: 'visual-artists', label: 'Visual Artists' },
  { value: 'performers', label: 'Performers' },
  { value: 'workshops-healing', label: 'Workshops & Healing' },
  { value: 'market-stalls', label: 'Market Stalls' },
  { value: 'volunteers', label: 'Volunteers' },
  { value: 'media', label: 'Media' },
  { value: 'spaces-theme-camps', label: 'Spaces & Theme Camps' },
  { value: 'vj-multimedia', label: 'VJ & Multimedia' },
];

export default function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedType, setSelectedType] = useState('');

  // Check if already logged in
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth) {
      setIsLoggedIn(true);
    }
  }, []);

  // Fetch data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const getAuthHeader = () => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth) {
      return { Authorization: `Basic ${savedAuth}` };
    }
    return {};
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const credentials = btoa(`${username}:${password}`);
      await axios.post(`${API}/admin/login`, {}, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      
      sessionStorage.setItem('adminAuth', credentials);
      setIsLoggedIn(true);
      toast.success('Welcome to Admin Dashboard');
    } catch (error) {
      toast.error('Invalid username or password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsLoggedIn(false);
    setStats(null);
    setApplications([]);
    setContacts([]);
    setNewsletters([]);
    toast.info('Logged out successfully');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [statsRes, appsRes, contactsRes, newslettersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/applications`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/newsletters`, { headers }),
      ]);
      
      setStats(statsRes.data);
      setApplications(appsRes.data);
      setContacts(contactsRes.data);
      setNewsletters(newslettersRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (appId, status) => {
    try {
      const headers = getAuthHeader();
      await axios.patch(`${API}/applications/${appId}/status?status=${status}`, {}, { headers });
      toast.success(`Application ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredApplications = selectedType 
    ? applications.filter(app => app.application_type === selectedType)
    : applications;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded">Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded">Rejected</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400 rounded">Pending</span>;
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-ff-background pt-20 flex items-center justify-center" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 w-full max-w-md mx-4"
        >
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 text-ff-secondary mx-auto mb-4" />
            <h1 className="font-display text-2xl text-ff-text-primary">Admin Login</h1>
            <p className="text-ff-text-secondary text-sm mt-2">Enter your credentials to access the dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="form-label">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                placeholder="admin"
                required
                data-testid="admin-username"
              />
            </div>
            
            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ff-text-muted hover:text-ff-text-secondary"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loginLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {loginLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Lock className="w-5 h-5" />
              )}
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Loading state
  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-ff-background pt-20 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-ff-secondary animate-spin" />
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-ff-background pt-20" data-testid="admin-page">
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-display text-3xl md:text-4xl text-ff-text-primary mb-2">
                Admin <span className="text-gold-gradient">Dashboard</span>
              </h1>
              <p className="text-ff-text-secondary">Manage applications and submissions</p>
            </div>
            <div className="flex gap-3 mt-4 md:mt-0">
              <button
                onClick={fetchData}
                className="btn-outline flex items-center gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-red-400 border border-red-400/30 rounded-full hover:bg-red-400/10 flex items-center gap-2 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6"
            >
              <FileText className="w-6 h-6 text-ff-secondary mb-2" />
              <p className="text-2xl font-bold text-ff-text-primary">{stats?.total_applications || 0}</p>
              <p className="text-sm text-ff-text-muted">Total Applications</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6"
            >
              <Clock className="w-6 h-6 text-yellow-500 mb-2" />
              <p className="text-2xl font-bold text-ff-text-primary">{stats?.pending_applications || 0}</p>
              <p className="text-sm text-ff-text-muted">Pending Review</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6"
            >
              <Mail className="w-6 h-6 text-ff-secondary mb-2" />
              <p className="text-2xl font-bold text-ff-text-primary">{stats?.contact_submissions || 0}</p>
              <p className="text-sm text-ff-text-muted">Contact Messages</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6"
            >
              <Users className="w-6 h-6 text-ff-secondary mb-2" />
              <p className="text-2xl font-bold text-ff-text-primary">{stats?.newsletter_subscriptions || 0}</p>
              <p className="text-sm text-ff-text-muted">Newsletter Subs</p>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {['overview', 'applications', 'contacts', 'newsletter'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm uppercase tracking-wider whitespace-nowrap transition-all rounded-full ${
                  activeTab === tab 
                    ? 'bg-ff-secondary text-ff-background' 
                    : 'glass text-ff-text-secondary hover:text-ff-secondary'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="glass-card p-6">
              <h3 className="font-display text-xl text-ff-text-primary mb-6">Applications by Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {applicationTypes.map((type) => (
                  <div key={type.value} className="bg-white/5 p-4 rounded">
                    <p className="text-ff-text-secondary text-sm">{type.label}</p>
                    <p className="text-2xl font-bold text-ff-text-primary">
                      {stats?.applications_by_type?.[type.value] || 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div>
              {/* Filter */}
              <div className="mb-6">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="form-input max-w-xs"
                  data-testid="application-filter"
                >
                  <option value="">All Applications</option>
                  {applicationTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Applications List */}
              <div className="space-y-4">
                {filteredApplications.length === 0 ? (
                  <div className="glass-card p-8 text-center">
                    <p className="text-ff-text-muted">No applications found</p>
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="space-y-2">
                    {filteredApplications.map((app) => (
                      <AccordionItem key={app.id} value={app.id} className="glass-card border-none">
                        <AccordionTrigger className="px-6 py-4 hover:no-underline">
                          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-left w-full pr-4">
                            <span className="text-ff-text-primary font-medium">
                              {app.first_name} {app.last_name}
                            </span>
                            <span className="text-ff-text-muted text-sm">
                              {applicationTypes.find(t => t.value === app.application_type)?.label || app.application_type}
                            </span>
                            {getStatusBadge(app.status)}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-6">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-display text-sm text-ff-secondary mb-2">Contact</h4>
                              <p className="text-ff-text-secondary text-sm">{app.email}</p>
                              <p className="text-ff-text-secondary text-sm">{app.phone}</p>
                              {app.address && (
                                <p className="text-ff-text-secondary text-sm mt-1">
                                  {app.address}, {app.city} {app.state} {app.postcode}
                                </p>
                              )}
                            </div>
                            
                            {app.social_media && (
                              <div>
                                <h4 className="font-display text-sm text-ff-secondary mb-2">Social</h4>
                                <p className="text-ff-text-secondary text-sm">{app.social_media}</p>
                              </div>
                            )}
                            
                            {app.experience && (
                              <div className="md:col-span-2">
                                <h4 className="font-display text-sm text-ff-secondary mb-2">Experience</h4>
                                <p className="text-ff-text-secondary text-sm whitespace-pre-wrap">{app.experience}</p>
                              </div>
                            )}
                            
                            {app.description && (
                              <div className="md:col-span-2">
                                <h4 className="font-display text-sm text-ff-secondary mb-2">Description</h4>
                                <p className="text-ff-text-secondary text-sm whitespace-pre-wrap">{app.description}</p>
                              </div>
                            )}
                            
                            {app.portfolio_links && (
                              <div className="md:col-span-2">
                                <h4 className="font-display text-sm text-ff-secondary mb-2">Portfolio</h4>
                                <p className="text-ff-text-secondary text-sm whitespace-pre-wrap">{app.portfolio_links}</p>
                              </div>
                            )}
                            
                            {app.file_urls && app.file_urls.length > 0 && (
                              <div className="md:col-span-2">
                                <h4 className="font-display text-sm text-ff-secondary mb-2">Uploaded Files</h4>
                                <div className="flex flex-wrap gap-2">
                                  {app.file_urls.map((url, i) => (
                                    <a
                                      key={i}
                                      href={`${BACKEND_URL}${url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-ff-secondary text-sm hover:underline"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      File {i + 1}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="md:col-span-2 pt-4 border-t border-white/10 flex gap-3">
                              <button
                                onClick={() => updateApplicationStatus(app.id, 'approved')}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors"
                                data-testid={`approve-${app.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                                Approve
                              </button>
                              <button
                                onClick={() => updateApplicationStatus(app.id, 'rejected')}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                                data-testid={`reject-${app.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                                Reject
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-ff-text-muted mt-4">
                            Submitted: {new Date(app.created_at).toLocaleString()}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              {contacts.length === 0 ? (
                <div className="glass-card p-8 text-center">
                  <p className="text-ff-text-muted">No contact messages</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div key={contact.id} className="glass-card p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-ff-text-primary font-medium">
                          {contact.first_name} {contact.last_name}
                        </h4>
                        <p className="text-ff-text-secondary text-sm">{contact.email}</p>
                        {contact.phone && (
                          <p className="text-ff-text-secondary text-sm">{contact.phone}</p>
                        )}
                      </div>
                      <span className="text-xs text-ff-text-muted">
                        {new Date(contact.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-ff-text-secondary whitespace-pre-wrap">{contact.message}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Newsletter Tab */}
          {activeTab === 'newsletter' && (
            <div className="glass-card p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-display text-xl text-ff-text-primary">
                  Newsletter Subscribers ({newsletters.length})
                </h3>
              </div>
              
              {newsletters.length === 0 ? (
                <p className="text-ff-text-muted text-center py-8">No subscribers yet</p>
              ) : (
                <div className="space-y-2">
                  {newsletters.map((sub) => (
                    <div key={sub.id} className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-ff-text-secondary">{sub.email}</span>
                      <span className="text-xs text-ff-text-muted">
                        {new Date(sub.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
